import { AuthProvider, AuthProviderState, AuthState, AmpAccessToken } from './Auth';
import { newOidcProvider } from './oidc';
import { REQUEST_ID_PATTERN } from '../pages/Broker/brokerSeam';

// The OIDC broker serves this same login page for its home-realm picker, so the
// portal's components are reused as-is and only the provider differs. The broker
// resolves the tenant server-side and redirects on to the realm, so none of the
// portal's metadata lookup, PKCE, or token handling applies here.

// The broker owns the submit, this page owns /oidc/login, so the two are
// separate paths: the ingress splits them by path alone, with no dependency on
// method matching.
const brokerSubmitPath = '/oidc/login/submit';

// requestIDParam is the broker's in-flight authorization request, minted by
// /oidc/authorize and carried in the URL it redirects the browser to.
const requestIDParam = 'request_id';

// brokerRequestID returns the broker flow id when this page was opened as the
// broker's picker, and null when it is the portal's own login.
export const brokerRequestID = (): string | null => {
  return new URLSearchParams(window.location.search).get(requestIDParam);
};

// crossTierParam is the broker's mark on a flow the cross-tier link may carry
// to the other tier: every gov broker flow, and a commercial one when that
// broker chains to gov.
const crossTierParam = 'crossTier';

// crossTierBrokerURL takes a marked broker flow to the other tier: the gov
// dispatch on commercial, the cancel on gov. It is absolute so the link leaves
// the SPA; null keeps the link to the other tier's portal.
export const crossTierBrokerURL = (isGov: boolean): string | null => {
  const params = new URLSearchParams(window.location.search);
  const requestID = params.get(requestIDParam);
  if (requestID === null || !REQUEST_ID_PATTERN.test(requestID) || params.get(crossTierParam) !== '1') {
    return null;
  }
  const target = new URLSearchParams({ [requestIDParam]: requestID });
  return `${window.location.origin}/oidc/${isGov ? 'cancel' : 'gov'}?${target.toString()}`;
};

// submitToBroker posts a real form rather than fetching: the browser has to
// follow the 303 itself. This document's form-action 'self' governs every hop
// of that chain, so the broker 303s to a same-origin seam, not to the realm.
const submitToBroker = (requestID: string, field: string, value: string): void => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = brokerSubmitPath;

  const fields: Record<string, string> = { [requestIDParam]: requestID, [field]: value };
  Object.entries(fields).forEach(([name, fieldValue]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = fieldValue;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  // The browser captures the submission synchronously, so the node goes back
  // out immediately rather than lingering if the navigation is cancelled.
  form.remove();
};

export const newBrokerProvider = (requestID: string): AuthProvider => {
  // The broker leg never carries a portal session: the user is signing in to a
  // relying party, and the id_token the broker mints is for that app, not for
  // the portal. So the page always opens at the sign-in step.
  const getInitialAuthState = async (): Promise<AuthProviderState> =>
    Promise.resolve({
      authState: AuthState.SIGN_IN,
      user: undefined,
      session: { username: '' },
    });

  // The identifier field takes an email or a mytanium.com username and branches
  // server-side, so every sign-in entry point posts the same field.
  const postIdentifier = async (identifier: string) => submitToBroker(requestID, 'email', identifier);
  const signInWithWorkspace = async (workspace: string) => submitToBroker(requestID, 'workspace', workspace);

  // No portal session exists on this leg, so there is nothing to end and no
  // token to hand out. Both are part of AuthProvider but unreachable from the
  // unauthenticated area the broker renders.
  const signOut = () => {};
  const getToken = async (): Promise<AmpAccessToken> => {
    throw new Error('the broker login has no portal session to issue a token for');
  };

  return {
    getInitialAuthState,
    signIn: postIdentifier,
    signInIdp: postIdentifier,
    signOut,
    getToken,
    signInWithWorkspace,
    signInIdpHelper: postIdentifier,
  };
};

// newLoginProvider picks the provider this page runs on. The broker redirects
// to the portal's login with a request_id; without one this is the portal's own
// login and nothing about the broker applies.
export const newLoginProvider = (): AuthProvider => {
  const requestID = brokerRequestID();
  return requestID !== null ? newBrokerProvider(requestID) : newOidcProvider();
};
